import type { ListBrokerageFirmPoliciesInputBody } from 'korint-sdk';
import { type PolicyColumnConfig, useKorintModuleConfig } from 'korint-sdk/react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { PolicyTable } from '../../components/module-elements/PolicyTable/PolicyTable';
import { Stack } from '../../components/ui/flex';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';

export const FLAGGED_STATUSES: NonNullable<ListBrokerageFirmPoliciesInputBody['fraudStatuses']> = [
  'fraud',
  'review_needed',
];

export const FRAUD_CONTROL_VIEWS = ['portfolio', 'ongoingNewBusiness'] as const;
type View = (typeof FRAUD_CONTROL_VIEWS)[number];

export function FraudControlModule() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const view: View =
    searchParams.get('view') === 'ongoingNewBusiness' ? 'ongoingNewBusiness' : 'portfolio';
  const config = useKorintModuleConfig(view);

  const fraudColumns: PolicyColumnConfig[] = [
    {
      name: 'fraudScore',
      type: 'string',
      accessor: ({ fraudSummary }) =>
        fraudSummary?.globalScore != null
          ? t('documentReview.fraud.scoreOutOf', { score: fraudSummary.globalScore })
          : null,
    },
    {
      name: 'flaggedDocuments',
      type: 'string',
      accessor: ({ fraudSummary }) =>
        fraudSummary &&
        t('fraudControl.flaggedDocuments', { count: fraudSummary.flaggedDocumentCount }),
    },
    { name: 'fraudStatus', type: 'fraudStatus', path: 'fraudSummary.globalStatus' },
  ];

  return (
    <Stack gap={4}>
      <Tabs value={view} onValueChange={value => setSearchParams({ view: value })}>
        <TabsList>
          {FRAUD_CONTROL_VIEWS.map(tab => (
            <TabsTrigger key={tab} value={tab}>
              {t(`fraudControl.tabs.${tab}`)}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      <PolicyTable
        key={view}
        filtersConfig={{
          ...config.filtersConfig,
          fraudStatus: { type: 'select', options: ['fraud', 'review_needed', 'clean'] },
        }}
        baseFilters={{ ...config.baseFilters, fraudStatuses: FLAGGED_STATUSES }}
        initialFilters={[{ field: 'fraudStatus', value: FLAGGED_STATUSES }]}
        expandCustomers={config.expandCustomers}
        expandAssets={config.expandAssets}
        expandBrokerageFirm={config.expandBrokerageFirm}
        columns={[...config.columns, ...fraudColumns]}
        policyLinkSuffix="/documents"
      />
    </Stack>
  );
}
